import React, { useEffect, useReducer, useRef, useState } from "react";
import client from "@culturehq/client";
import { Loader, PlainButton } from "@culturehq/components";

import { Trans } from "app/utils";

import ErrorBoundary from "components/common/ErrorBoundary";
import ErrorMessage from "components/common/ErrorMessage";
import FeedItemPlaceholder from "components/feed/FeedItemPlaceholder";
import FeedItem from "components/feed/FeedItem";

const initialState = {
  appending: null,
  error: null,
  fetching: false,
  pagination: {},
  userActivities: []
};

const reducer = (state, action) => {
  switch (action.type) {
    case "appendStart":
      return { ...state, appending: state.pagination.currentPage + 1 };
    case "appendSuccess":
      return {
        ...state,
        appending: null,
        pagination: action.response.pagination,
        userActivities: [...state.userActivities, ...action.response.userActivities]
      };
    case "appendError":
      return { ...state, appending: null, error: action.error };
    case "fetchStart":
      return { ...state, error: null, fetching: true };
    case "fetchSuccess":
      return {
        ...state,
        error: null,
        fetching: false,
        pagination: action.response.pagination,
        userActivities: action.response.userActivities
      };
    case "fetchError":
      return { ...state, error: action.error, fetching: false };
    case "userActivityUpdate":
      return {
        ...state,
        userActivities: state.userActivities.map(userActivity => {
          if (userActivity.id === action.userActivityId) {
            return { ...userActivity, ...action.updates };
          }
          return userActivity;
        })
      };
    case "userActivityRemove":
      return {
        ...state,
        userActivities: state.userActivities.map(userActivity => (
          userActivity.childUserActivities.filter(child => (
            child.id !== action.userActivityId
          ))
        )).filter(userActivity => (
          userActivity.id !== action.userActivityId
        ))
      };
    default:
      return state;
  }
};

const useFetchUserActivities = (dispatch, types, user) => useEffect(
  () => {
    let cancelled = false;
    dispatch({ type: "fetchStart" });

    client.listUserActivities({ types, for_user: user.id })
      .then(response => {
        if (!cancelled) {
          dispatch({ type: "fetchSuccess", response });
        }
      })
      .catch(error => {
        if (!cancelled) {
          dispatch({ type: "fetchError", error });
        }
      });

    return () => {
      cancelled = true;
    };
  },
  [dispatch, types, user]
);

const useAppendUserActivities = (dispatch, types, appending, user) => useEffect(
  () => {
    if (appending) {
      let cancelled = false;

      client.listUserActivities({ types, page: appending, for_user: user.id })
        .then(response => {
          if (!cancelled) {
            dispatch({ type: "appendSuccess", response });
          }
        })
        .catch(error => {
          if (!cancelled) {
            dispatch({ type: "appendError", error });
          }
        });

      return () => {
        cancelled = true;
      };
    }

    return undefined;
  },
  [dispatch, types, appending, user]
);

const useScrollListen = (dispatch, feedRef, pagination) => useEffect(
  () => {
    if (pagination.currentPage !== pagination.totalPages) {
      const onScroll = () => {
        const feedBottom = feedRef.current.offsetTop + feedRef.current.offsetHeight;
        const windowBottom = window.pageYOffset + window.innerHeight;

        if ((feedBottom - windowBottom) < 100) {
          dispatch({ type: "appendStart" });
        }
      };

      document.addEventListener("scroll", onScroll);
      return () => document.removeEventListener("scroll", onScroll);
    }

    return undefined;
  },
  [dispatch, feedRef, pagination]
);

const FeedItems = ({ feedRef, onFilterClear, types, user }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useFetchUserActivities(dispatch, types, user);
  useAppendUserActivities(dispatch, types, state.appending, user);
  useScrollListen(dispatch, feedRef, state.pagination);

  if (state.fetching) {
    return (
      <>
        <FeedItemPlaceholder />
        <FeedItemPlaceholder />
        <FeedItemPlaceholder />
      </>
    );
  }

  if (state.error) {
    return <ErrorMessage error={state.error} />;
  }

  const onUserActivityUpdate = (userActivityId, updates) => (
    dispatch({ type: "userActivityUpdate", userActivityId, updates })
  );

  const onUserActivityRemove = userActivityId => (
    dispatch({ type: "userActivityRemove", userActivityId })
  );

  return (
    <>
      {state.userActivities.map(userActivity => (
        <ErrorBoundary key={userActivity.id}>
          <FeedItem
            item={userActivity}
            onUserActivityUpdate={onUserActivityUpdate}
            onUserActivityRemove={onUserActivityRemove}
          />
        </ErrorBoundary>
      ))}
      {state.appending && (
        <Loader loading />
      )}
      {state.userActivities.length < 1 && (
        <div className="EmptyResults EmptyResults-feed">
          <p><Trans>No posts to show...</Trans></p>
          {types && (
            <PlainButton onClick={onFilterClear}>
              <Trans>Start over</Trans>
            </PlainButton>
          )}
        </div>
      )}
    </>
  );
};

const UserFeed = ({ user }) => {
  const [types, setTypes] = useState(null);
  const onFilterClear = () => setTypes(null);

  const feedRef = useRef();

  return (
    <div className="row User--Feed" ref={feedRef}>
      <div className="Feed--column col-xs-12">
        <ErrorBoundary>
          <FeedItems
            feedRef={feedRef}
            onFilterClear={onFilterClear}
            types={types}
            user={user}
          />
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default UserFeed;
